import { useState } from "react";
import { MatchResponseDto } from "@/lib/api/interfaces/matches.interface";
import { useMatchesFindQuery } from "@/lib/api/requests/matches.requests";
import { formatFriendlyDate } from "@/lib/utils/date-formatter";
import { Button } from "@/ui-components/ui/button";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { BarChart3, History } from "lucide-react";
import { DefaultTemplateType } from "@/page-components/post-generator/template-editor/registry/defaultTemplates";
import { MissingTemplateDialog } from "../../../util-components/dialog/MissingTemplateDialog";
import MatchDetailsDialog from "@/page-components/matches/details-dialog";
import { useDefaultTemplate } from "@/lib/hooks/use-default-template";

const PassedGames = () => {
    const { data: matches, isLoading } = useMatchesFindQuery({ limit: 4, filterType: "past" });

    if (isLoading || !matches) {
        return <PassedGamesSkeleton />
    }

  return (
    <div className="divide-y divide-gray-100">
        {matches?.matches.docs.map((match) => (
            <PassedGame 
                key={match._id} 
                match={match} 
            />
        ))}
        {(!matches?.matches.docs || matches.matches.docs.length === 0) && (
          <div className="p-8 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 mb-3 text-gray-400">
                 <History className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-gray-900">Keine vergangenen Spiele</p>
          </div>
        )}
    </div>
  );
};

const PassedGame = ({ match }: { match: MatchResponseDto }) => {
  const { handleCreatePost, isLoading: isLoadingDefaults, missingTemplateDialogProps } = useDefaultTemplate({
    prefillData: {
      entityType: 'Match',
      entityId: match._id
    }
  });

  const [detailsOpen, setDetailsOpen] = useState(false);

  const teamName = match.team?.name || "?";
  const opponentName = match.opponent?.name || "Unbekannter Gegner";
  const matchType = match.homeOrAway === 'AWAY' ? 'Auswärtsspiel' : 'Heimspiel';

  const isAway = match.homeOrAway === 'AWAY';
  const homeThumbnail = isAway
    ? match.opponent?.thumbnailUrl || match.opponent?.logoUrl
    : match.team?.thumbnailUrl || match.team?.logoUrl;
  const awayThumbnail = isAway
    ? match.team?.thumbnailUrl || match.team?.logoUrl
    : match.opponent?.thumbnailUrl || match.opponent?.logoUrl;

  // Match status badge variant
  let statusColor = "text-gray-500";
  let statusBg = "bg-gray-100";
  
  const scoreText = match.ourScore !== undefined && match.ourScore !== null &&
                    match.opponentScore !== undefined && match.opponentScore !== null
    ? (match.homeOrAway === 'AWAY' ? `${match.opponentScore}:${match.ourScore}` : `${match.ourScore}:${match.opponentScore}`)
    : "?:?";

  if (match.ourScore !== undefined && match.opponentScore !== undefined) {
    if (match.ourScore > match.opponentScore) {
      statusColor = "text-green-700";
      statusBg = "bg-green-100";
    } else if (match.ourScore < match.opponentScore) {
      statusColor = "text-red-700";
      statusBg = "bg-red-100";
    } else {
      statusColor = "text-gray-700";
      statusBg = "bg-gray-100";
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    handleCreatePost(DefaultTemplateType.MATCH_RECAP, "Endstand", e);
  };
  
  return (
    <>
        <div 
            onClick={() => setDetailsOpen(true)}
            className="group relative flex flex-col sm:flex-row sm:items-center justify-between p-4 hover:bg-gray-50 transition-colors gap-4 cursor-pointer"
        >
            <div className="flex items-center gap-4 min-w-0">
                <div className="flex items-center shrink-0">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center overflow-hidden z-10">
                        {homeThumbnail ? (
                            <img src={homeThumbnail} alt="Home Logo" className="w-full h-full object-contain p-1.5" />
                        ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 font-bold text-xs">?</div>
                        )}
                    </div>
                    <div className="h-10 w-10 shrink-0 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center overflow-hidden -ml-3 z-0">
                        {awayThumbnail ? (
                            <img src={awayThumbnail} alt="Away Logo" className="w-full h-full object-contain p-1.5" />
                        ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 font-bold text-xs">?</div>
                        )}
                    </div>
                </div>
                
                <div className="min-w-0 flex-1">
                    <div className="flex flex-col mb-0.5">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">
                            {teamName}
                        </span>
                        <span className="font-semibold text-gray-900 truncate text-base flex items-center gap-2">
                            vs. {opponentName}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusBg} ${statusColor}`}>
                                {scoreText}
                            </span>
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>{formatFriendlyDate(match.date)}</span>
                        <span>•</span>
                        <span>{matchType}</span>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity sm:absolute sm:right-0 sm:top-0 sm:bottom-0 sm:pr-4 sm:pl-24 sm:bg-gradient-to-r sm:from-transparent sm:via-gray-50 sm:to-gray-50">
                <Button
                size="sm"
                variant="outline"
                disabled={isLoadingDefaults}
                className="relative z-10 h-10 text-xs bg-white px-4 rounded-xl shadow-sm border-gray-200 hover:bg-gray-50 transition-all"
                onClick={handleClick}
                >
                <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
                Grafik erstellen
                </Button>
            </div>
        </div>
        <MissingTemplateDialog
            {...missingTemplateDialogProps}
        />
        <MatchDetailsDialog 
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            matchId={match._id}
        />
    </>
  );
};

const PassedGamesSkeleton = () => {
    return (
        <div className="divide-y divide-gray-100">
            {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                    </div>
                    <div>
                        <Skeleton className="h-8 w-24" />
                    </div>
                </div>
            ))}
        </div>
    )
}

export default PassedGames;
